import { Export } from '@phosphor-icons/react';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { MonthlyStatement } from '../../../../../../types/dbTypes';
import useApi from '../../../../../../utils/AxiosWrapper';
import { useState } from 'react';
import { ClipLoader } from 'react-spinners';
import { useNavigate } from 'react-router-dom';
import { DemoDataWrapper } from '../../home/components/DemoDataBadge';
import toast from 'react-hot-toast';
import { useDemo } from '../../../../../../utils/DemoContext';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function StatementHistoryWidget({
  monthlyStatements,
  loadingMonthlyStatements,
  isPreActive = false,
}: {
  monthlyStatements: MonthlyStatement[];
  loadingMonthlyStatements: boolean;
  isPreActive?: boolean;
}) {
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  const navigate = useNavigate();
  const { isDemo } = useDemo();
  const [loadingMap, setLoadingMap] = useState<Map<string, boolean>>(new Map());
  const downloadPdf = async (statementId: string) => {
    setLoadingMap((prev) => new Map(prev).set(statementId, true));
    try {
      const response = await callProtectedApi(
        `/documents/display/monthlyStatement/${statementId}`,
        'get',
        {},
        'Error occurred while downloading CSV'
      );
      const presigned_url = response?.data?.data?.presigned_url;
      if (presigned_url) {
        window.open(presigned_url, '_blank');
      }
    } catch (error: any) {
      console.error('Error occurred while downloading PDF: ', error);
    } finally {
      setLoadingMap((prev) => new Map(prev).set(statementId, false));
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-[385px] max-h-[385px] w-full flex-col items-start border-[1px] border-bordergray bg-white align-middle lg:max-w-[375px]">
            <div className="flex w-full flex-row items-center justify-between border-b-[1px] border-bordergray p-4 align-middle">
              <h2 className="text-[16px] font-semibold leading-[24px] text-primary">
                Statement History
              </h2>
              <h2
                className={`text-[16px] font-semibold underline ${isDemo ? 'cursor-not-allowed opacity-50' : 'hover:cursor-pointer'}`}
                onClick={
                  isDemo
                    ? undefined
                    : () => navigate('/dashboard/banking/statements')
                }
              >
                Show All
              </h2>
            </div>
            {monthlyStatements && !loadingMonthlyStatements ? (
              <>
                {monthlyStatements?.length > 0 ? (
                  <div className="flex w-full flex-col items-start justify-start space-y-4 overflow-y-auto overflow-x-hidden p-4">
                    <div className="flex w-full flex-col items-start space-y-1 rounded-[8px] border-[1px] border-bordergray p-4">
                      <h2 className="text-[14px] text-primary">
                        Current Statement
                      </h2>
                      <p className="text-[12px] font-[400] text-[#858897]">
                        {`${MONTHS[monthlyStatements[0]?.month - 1]} ${monthlyStatements[0]?.year}`}
                      </p>
                      <div
                        onClick={async () => {
                          if (!isPreActive) {
                            await downloadPdf(
                              monthlyStatements[0]?.statement_id ?? ''
                            );
                          } else {
                            toast.error(
                              'Please complete your onboarding steps to access monthly statements.'
                            );
                            return;
                          }
                        }}
                        className="flex flex-row items-center space-x-2 align-middle text-accent hover:cursor-pointer"
                      >
                        <Export size={20} />
                        <p>Statement.PDF</p>
                        {loadingMap.get(monthlyStatements[0]?.statement_id) && (
                          <ClipLoader
                            color="#1D213B"
                            loading={loadingMap.get(
                              monthlyStatements[0]?.statement_id ?? ''
                            )}
                            size={20}
                            aria-label="Loading Spinner"
                            data-testid="loader"
                            className="flex h-full w-full items-center justify-center"
                          />
                        )}
                      </div>
                    </div>
                    <div className="flex w-full flex-col space-y-2">
                      {monthlyStatements &&
                        monthlyStatements?.slice(1)?.map((statement) => (
                          <div
                            key={statement?.statement_id}
                            className="flex w-full flex-col items-start justify-start space-y-4 overflow-y-auto overflow-x-hidden"
                          >
                            <div
                              onClick={async () => {
                                if (!isPreActive) {
                                  await downloadPdf(
                                    statement?.statement_id ?? ''
                                  );
                                } else {
                                  toast.error(
                                    'Please complete your onboarding steps to access monthly statements.'
                                  );
                                  return;
                                }
                              }}
                              className="flex w-full flex-row items-start space-x-1 rounded-[8px] border-[1px] border-bordergray p-4 hover:cursor-pointer hover:bg-gray-50"
                            >
                              <Export size={20} />
                              <h2 className="text-[14px] text-primary">
                                {`${MONTHS[statement?.month - 1]} ${statement?.year}`}
                              </h2>
                              {loadingMap.get(statement?.statement_id) && (
                                <ClipLoader
                                  color="#1D213B"
                                  loading={loadingMap.get(
                                    statement?.statement_id ?? ''
                                  )}
                                  size={20}
                                  aria-label="Loading Spinner"
                                  data-testid="loader"
                                  className="flex h-full w-full items-center justify-center"
                                />
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center text-center">
                    <div className="w-[60%]">
                      <p className="text-[16px] font-[500] leading-[24px] text-primary">
                        No Statements Available
                      </p>
                      <p className="text-[12px] font-[400] leading-[20px] text-graysecondary">
                        Once transactions are processed, you'll find them here.
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loadingMonthlyStatements}
                  size={screenView === 'mobile' ? 40 : 60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="flex h-full w-full items-center justify-center"
                />
              </div>
            )}
          </div>
        );
    }
  };
  return (
    <DemoDataWrapper showBadge={isPreActive}>
      {renderScreenView(screenView)}
    </DemoDataWrapper>
  );
}

export default StatementHistoryWidget;
